import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { RefreshCw, Send, Loader2, Clock, CheckCircle, Truck, ArrowRight, XCircle, ShieldCheck, Wallet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusSteps = [
  { key: 'pending_approval', label: 'Por Aprobar', icon: ShieldCheck },
  { key: 'processing', label: 'Procesando', icon: RefreshCw },
  { key: 'sent', label: 'Enviado', icon: Truck },
  { key: 'completed', label: 'Completado', icon: CheckCircle },
];

const DivisaProgressBar = ({ status }) => {
  if (status === 'rejected') {
    return (
      <div className="mt-3 flex items-center gap-2 text-red-600">
        <XCircle className="w-5 h-5" />
        <span className="text-sm font-medium">Rechazada - Monto reembolsado</span>
      </div>
    );
  }
  const idx = statusSteps.findIndex(s => s.key === status);
  return (
    <div className="flex items-center gap-1 mt-3">
      {statusSteps.map((step, i) => (
        <div key={step.key} className="flex items-center flex-1">
          <div className={`flex items-center gap-1 ${i <= idx ? 'text-blue-600' : 'text-slate-300'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 ${i <= idx ? 'bg-blue-500 border-blue-500 text-white' : 'border-slate-300'}`}>
              {i < idx ? <CheckCircle className="w-4 h-4" /> : i + 1}
            </div>
            <span className="text-[10px] font-medium hidden sm:inline">{step.label}</span>
          </div>
          {i < statusSteps.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${i < idx ? 'bg-blue-500' : 'bg-slate-200'}`} />}
        </div>
      ))}
    </div>
  );
};

const ClientDivisasPage = () => {
  const { getAuthHeaders } = useAuth();
  const [currencies, setCurrencies] = useState([]);
  const [history, setHistory] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [amount, setAmount] = useState('');
  const [calcResult, setCalcResult] = useState(null);
  const [form, setForm] = useState({ recipient_name: '', recipient_details: '' });
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState('send');

  const load = async () => {
    try {
      const h = { headers: getAuthHeaders() };
      const [c, hist, w] = await Promise.all([
        axios.get(`${API}/client/divisas/currencies`, h),
        axios.get(`${API}/client/divisas/history`, h),
        axios.get(`${API}/client/wallet/summary`, h),
      ]);
      setCurrencies(c.data);
      setHistory(hist.data);
      setWallet(w.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const calculate = async () => {
    if (!selectedCurrency || !amount) return;
    try {
      const res = await axios.post(`${API}/client/divisas/calculate`, {
        to_currency: selectedCurrency.code, amount: parseFloat(amount)
      }, { headers: getAuthHeaders() });
      setCalcResult(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const sendDivisa = async () => {
    if (!selectedCurrency || !amount) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/client/divisas/send`, {
        to_currency: selectedCurrency.code,
        amount: parseFloat(amount),
        recipient_name: form.recipient_name,
        recipient_details: form.recipient_details
      }, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      setAmount('');
      setCalcResult(null);
      setForm({ recipient_name: '', recipient_details: '' });
      setSelectedCurrency(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setSending(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  const balance = wallet?.balance || 0;
  const parsedAmount = parseFloat(amount) || 0;
  const insufficientFunds = parsedAmount > 0 && parsedAmount > balance;

  return (
    <div className="space-y-6" data-testid="client-divisas-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Divisas y Remesas</h1>
        <p className="text-slate-500">Envia dinero en diferentes monedas</p>
      </div>

      {/* Balance */}
      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2" data-testid="wallet-balance-divisas">
        <Wallet className="w-5 h-5 text-emerald-600" />
        <span className="text-sm text-emerald-700 font-medium">Saldo disponible: ${balance.toFixed(2)} USD</span>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button variant={tab === 'send' ? 'default' : 'outline'} size="sm" onClick={() => setTab('send')} className={tab === 'send' ? 'bg-blue-700' : ''} data-testid="tab-send">
          <Send className="w-4 h-4 mr-1" /> Enviar
        </Button>
        <Button variant={tab === 'history' ? 'default' : 'outline'} size="sm" onClick={() => setTab('history')} className={tab === 'history' ? 'bg-blue-700' : ''} data-testid="tab-history">
          <Clock className="w-4 h-4 mr-1" /> Historial
        </Button>
      </div>

      {tab === 'send' && (
        <>
          {/* Currency Selection */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {currencies.map((c) => (
              <Card key={c.code}
                className={`p-4 border-2 cursor-pointer transition-all text-center ${selectedCurrency?.code === c.code ? 'border-blue-500 bg-blue-50 shadow-md' : c.available ? 'border-slate-200 hover:border-blue-300' : 'border-slate-100 opacity-50 cursor-not-allowed'}`}
                onClick={() => c.available && setSelectedCurrency(c)}
                data-testid={`currency-${c.code}`}
              >
                <div className="text-2xl mb-1">{c.icon}</div>
                <p className="font-semibold text-sm text-slate-900">{c.code}</p>
                <p className="text-[10px] text-slate-500">{c.name}</p>
                {c.rate && <p className="text-xs text-blue-600 mt-1 font-medium">1 USD = {c.rate}</p>}
              </Card>
            ))}
          </div>

          {/* Send Form */}
          {selectedCurrency && (
            <Card className="p-6 border-blue-200 bg-blue-50" data-testid="divisa-form">
              <h3 className="font-semibold text-slate-900 mb-4">Enviar a: {selectedCurrency.name}</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monto a enviar (USD)</Label>
                  <div className="flex gap-2">
                    <Input type="number" step="0.01" min="1" value={amount} onChange={(e) => { setAmount(e.target.value); setCalcResult(null); }} placeholder="100.00" data-testid="divisa-amount" />
                    <Button onClick={calculate} variant="outline" className="shrink-0" data-testid="divisa-calc-btn">Calcular</Button>
                  </div>
                  {insufficientFunds && (
                    <p className="text-xs text-red-600 font-medium" data-testid="divisa-insufficient">Saldo insuficiente. Tienes ${balance.toFixed(2)}</p>
                  )}
                </div>
                {calcResult && (
                  <div className="p-4 bg-white rounded-xl border-2 border-blue-300" data-testid="divisa-calc-result">
                    <p className="text-sm text-slate-500">Destinatario recibe:</p>
                    <p className="text-2xl font-bold text-blue-700">{calcResult.receive.toFixed(2)} {calcResult.receive_currency}</p>
                    <p className="text-xs text-slate-400 mt-1">Tasa: 1 USD = {calcResult.rate} {calcResult.receive_currency}</p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Nombre del destinatario</Label>
                  <Input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} placeholder="Nombre completo" data-testid="divisa-recipient" />
                </div>
                <div className="space-y-2">
                  <Label>Datos del destinatario</Label>
                  <Input value={form.recipient_details} onChange={(e) => setForm({ ...form, recipient_details: e.target.value })} placeholder="Telefono, direccion, # cuenta..." data-testid="divisa-details" />
                </div>
              </div>
              <div className="mt-4 p-3 bg-white/60 rounded-lg border border-blue-200 text-sm text-slate-600 mb-3">
                <div className="flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-blue-600" />
                  <span>Se descontara <strong>${parsedAmount.toFixed(2)}</strong> de tu wallet. Saldo restante: <strong>${(balance - parsedAmount).toFixed(2)}</strong></span>
                </div>
              </div>
              <Button onClick={sendDivisa} disabled={sending || !amount || !calcResult || insufficientFunds} className="bg-blue-700 hover:bg-blue-800 disabled:opacity-50" data-testid="divisa-send-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wallet className="w-4 h-4 mr-2" /> Confirmar y Pagar con Wallet</>}
              </Button>
              <p className="text-xs text-slate-500 mt-2">La operacion quedara pendiente de aprobacion por un administrador.</p>
            </Card>
          )}
        </>
      )}

      {tab === 'history' && (
        <div className="space-y-3">
          {history.length === 0 ? (
            <Card className="p-12 text-center border-0 shadow-sm">
              <RefreshCw className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Sin operaciones de divisas</p>
            </Card>
          ) : (
            history.map((op) => (
              <Card key={op.id} className={`p-4 border-0 shadow-sm ${op.status === 'rejected' ? 'bg-red-50/50' : ''}`} data-testid={`divisa-op-${op.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">{op.from_currency} → {op.to_currency}</p>
                    {op.recipient_name && <p className="text-sm text-slate-600">Para: {op.recipient_name}</p>}
                    <p className="text-xs text-slate-500 mt-1">{new Date(op.created_at).toLocaleString('es-ES')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-600">Enviaste: <strong>${op.send_amount?.toFixed(2)}</strong></p>
                    <p className="font-bold text-blue-700">{op.receive_amount?.toFixed(2)} {op.to_currency}</p>
                    {op.status === 'rejected' && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 mt-1 inline-block">Rechazada</span>
                    )}
                  </div>
                </div>
                <DivisaProgressBar status={op.status} />
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ClientDivisasPage;
